import React from 'react';
import { Button, LightChip, MoneyCents } from '@uplift/ui-v2';
import { ServiceItemPaymentStatus, PatientServiceItemInsurancePayment, PatientServiceItemPayment, PatientServiceItemStatus } from '@uplift/api';
import { Box, Divider, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useBillingContext } from '../../billing/billing.context';
import { CalendarDateService } from '@uplift/services';
import { mobileBreakpoint, useIsMobile } from '@uplift/hooks';


const useStyles = makeStyles()(theme => ({
  refundedRow: {
    color: theme.palette.legacyPrimary.main,
    fontWeight: 500,
  }
}));


export type BillingResponsibilityItem = {
  title: string;
  value: number | string;
};


export const getInsuranceBillingReportItems = (serviceItemPayment: PatientServiceItemInsurancePayment) => {
  const result = [];

  const { claim, status, payment } = serviceItemPayment;

  if (status === ServiceItemPaymentStatus.unknown) {
    return [];
  } else if (status === ServiceItemPaymentStatus.processing) {
    if (claim) {
      result.push({ title: 'Total charge', value: claim.chargeAmountCents });
      result.push({ title: 'Allowable amount', value: claim.totalAllowedAmountCents });
    } else {
      result.push({ title: 'Total charge', value: 'Processing' });
      result.push({ title: 'Allowable amount', value: 'Processing' });
    }
    result.push({ title: 'Paid by insurance', value: 'Processing' });
    result.push({ title: 'Your responsibility', value: 'Processing' });
  } else {
    if (claim) {
      result.push({ title: 'Total charge', value: claim.chargeAmountCents });
      result.push({ title: 'Allowable amount', value: claim.totalAllowedAmountCents });
      result.push({ title: 'Paid by insurance', value: claim.amountInsurancePaidCents });
      result.push({ title: 'Your responsibility', value: payment?.amountCents });
    }
  }

  return result;
};


const BillingReport: React.VFC<{
  billing: PatientServiceItemPayment;
  responsibilities: BillingResponsibilityItem[];
}> = ({ billing, responsibilities }) => {
  const { classes } = useStyles();
  const amountPaidRow = React.useMemo(() => {
    if (billing?.status === ServiceItemPaymentStatus.canceled) {
      return (
        <tr style={{ fontWeight: 500 }}>
          <td style={{ padding: '8px 0' }}>Amount due:</td>
          <td align="right">
            <MoneyCents value={0} />
          </td>
        </tr>
      );
    }

    if (billing?.status === ServiceItemPaymentStatus.processing) {
      return (
        <tr style={{ fontWeight: 500 }}>
          <td style={{ padding: '8px 0' }}>Amount due:</td>
          <td align="right">
            TBD
          </td>
        </tr>
      );
    }

    if (billing?.status === ServiceItemPaymentStatus.refunded) {
      const refundedDate = CalendarDateService.date(billing.payment.refundedAt).format('MM/DD/YYYY');

      return (
        <>
          <tr style={{ fontWeight: 500 }}>
            <td style={{ paddingTop: '8px', paddingBottom: '0px' }}>You paid:</td>
            <td style={{ paddingTop: '8px', paddingBottom: '0px' }} align="right">
              <MoneyCents value={billing?.payment?.amountCents} />
            </td>
          </tr>
          <tr className={classes.refundedRow}>
            <td style={{ paddingTop: '0px' }}>
              Refund total:
              <Box>
                <LightChip
                  size="small"
                  label={`Refunded on ${refundedDate}`}
                  color="success"
                  style={{
                    fontWeight: 400,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                  }}
                />
              </Box>
            </td>
            <td style={{ paddingTop: '0px', verticalAlign: 'top' }} align="right">
              <MoneyCents value={billing?.payment?.amountCents} />
            </td>
          </tr>
        </>
      );
    }

    if (billing?.status === ServiceItemPaymentStatus.completed) {
      const completedAt = CalendarDateService.date(billing.payment.completedAt).format('MM/DD/YYYY');

      return (
        <tr style={{ fontWeight: 500 }}>
          <td style={{ padding: '8px 0' }}>
            Amount due:
            <Box>
              <LightChip
                size="small"
                label={`Paid on ${completedAt}`}
                color="success"
                style={{
                  fontWeight: 400,
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                }}
              />
            </Box>
          </td>
          <td align="right" style={{ padding: '8px 0', verticalAlign: 'top' }}>
            <MoneyCents value={0} />
          </td>
        </tr>
      );
    }

    return (
      <tr style={{ fontWeight: 500 }}>
        <td style={{ padding: '8px 0' }}>Amount due:</td>
        <td align="right">
          <MoneyCents value={billing?.payment?.amountCents} />
        </td>
      </tr>
    );
  }, [ billing, classes ]);

  return (
    <table style={{ width: '100%', marginTop: 8 }}>
      {responsibilities.map((item, index) => (
        <tr key={index}>
          <td style={{ padding: '2px 0' }}>{item.title}:</td>
          <td align="right">
            {typeof item.value === 'number' && <MoneyCents value={item.value} />}
            {typeof item.value === 'string' && item.value}
          </td>
        </tr>
      ))}
      {amountPaidRow}
    </table>
  );
};


export const BillingDetails: React.VFC<{
  serviceItemStatus: PatientServiceItemStatus;
  billing: PatientServiceItemPayment;
  responsibilities: BillingResponsibilityItem[];
}> = ({ serviceItemStatus, billing, responsibilities }) => {
  const isMobile = useIsMobile(mobileBreakpoint);
  const billingContext = useBillingContext();

  return (
    <Box maxWidth={{ xs: '100%', sm: '284px' }}>
      {isMobile
        ? (
          <>
            <Box px={2} py={1}>
              <Typography variant="h4" color="textPrimary">Billing Details</Typography>
            </Box>
            <Divider />
          </>
        )
        : (
          <Typography variant="body2" color="textPrimary" style={{ fontWeight: 600 }}>Billing Details</Typography>
        )}
      {responsibilities?.length ? (
        <Box p={{ xs: 2, sm: 0 }} bgcolor={{ xs: '#fff', sm: 'transparent' }}>
          <BillingReport billing={billing} responsibilities={responsibilities} />
          {serviceItemStatus === PatientServiceItemStatus.outstanding && (
            <Box mt={1}>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={() => {
                  billingContext.setPaymentsToPay([ billing.payment ]);
                  billingContext.setPayBalanceOnly(true);
                  if (billingContext.cardExpiredOrDeclined) {
                    billingContext.setShowEditPaymentMethodModal(true);
                  } else {
                    billingContext.setShowPayBalanceModal(true);
                  }
                }}
              >
                pay this balance
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Box mt={{ xs: 0, sm: 2 }} px={{ xs: 2, sm: 0 }} py={{ xs: 3, sm: 0 }} bgcolor={{ xs: '#fff', sm: 'transparent' }}>
          <Typography
            variant={isMobile ? 'body1' : 'body2'}
            color="textSecondary"
          >Billing details are unavailable</Typography>
        </Box>
      )}
      {isMobile && <Divider />}
    </Box>
  );
};
